import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { useLocation } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { Flex, FlexColumn, FlexRow, Margin } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { capitalize, formatLocale } from '@/modules/shared/helpers/locale'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'

import type { FC, ReactNode } from 'react'
import type { TimelineActivity } from '@/modules/timeline/components/timeline/Timeline'
import type { PartialRecord } from '@/utils/typescript'
import type { ActivityCardProps } from './ActivityCard.types'

// Only appointments and encounters are rendered as an ActivityCard (see RelatedTriggers)
const cardIcons: PartialRecord<ActivityCardProps['type'], ReactNode> = {
  Appointment: <FontAwesomeIcon icon={['fal', 'calendar-day']} />,
  Intake: <FontAwesomeIcon icon={['fal', 'house-medical']} />,
}

const ActivityCard: FC<TimelineActivity> = (props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const location = useLocation()

  // Template variables
  // ------------------------------------------------------------------------- /
  const { id, type, carepath, description, scheduledTime, showStart, waitTime, onClick } = props

  const cardIcon = cardIcons[type]

  const itemIsSelected = location.pathname.includes(id.toString())

  // Render
  // ------------------------------------------------------------------------- /

  return (
    <StActivityCardContainer $type={type} $selected={itemIsSelected} onClick={onClick}>
      <div id={`card_${id}`} style={{ position: 'absolute', top: '-60px' }} />
      <FlexRow alignItems='center'>
        {cardIcon}
        <Margin ml={theme.UI.Spacing.Space2} />

        <FlexRow flexGrow={1} alignItems='center' justifyContent='space-between'>
          <H4 color={theme.Components.Card.ActiveText} noMargin>
            {i18n._(activityTypeTranslations[type] as string)}
          </H4>
          {scheduledTime && showStart ? (
            <Span type='BodyLargeBold'>
              {formatLocale(scheduledTime, 'HH:mm')}
              {waitTime ? (
                <Span type='BodyMediumRegular' color={theme.Colors.Status.Error}>
                  {waitTime}
                </Span>
              ) : null}
            </Span>
          ) : null}
        </FlexRow>
      </FlexRow>

      {description ? (
        <StActivityCardContent>
          {description.map((value, index) => {
            return (
              <Flex justifyContent='space-between' alignItems='center' key={`${value}_${index}`}>
                <StDescription type={value.type} color={value.color ?? theme.Components.Card.ActiveText}>
                  {value.text}
                </StDescription>

                {index === description.length - 1 ? (
                  <FontAwesomeIcon icon={['fal', 'chevron-right']} fontSize={16} />
                ) : null}
              </Flex>
            )
          })}
        </StActivityCardContent>
      ) : null}

      {carepath ? <Chip type='Primary'>{capitalize(carepath)}</Chip> : null}
    </StActivityCardContainer>
  )
}

export { ActivityCard }

// Styled components
const StActivityCardContainer = styled.div<{
  $type: ActivityCardProps['type']
  $selected: boolean
}>`
  ${({ theme, $type, $selected }) => {
    const categoryColor = theme.Colors.Category[$type as keyof typeof theme.Colors.Category]

    return `
      position: relative;
      width: 100%;
      color: ${theme.Components.Card.Text};
      background-color: ${theme.Components.Card.Fill};

      display: flex;
      flex-direction: column;
      gap: ${theme.UI.SpacingPx.Space3};

      border-width: 1px;
      border-style: solid;
      border-radius: ${theme.UI.BorderRadius.Large}px;
      border-color: ${categoryColor};
      padding: ${theme.UI.SpacingPx.Space4};
      overflow: hidden;

      @media ${breakpoints.s} {
        border-width: 2px;
        border-color: ${$selected ? categoryColor : theme.Components.Card.Border};

        &:hover {
          cursor: pointer;
          ${$selected ? '' : `border-color: ${theme.Colors.Neutrals.Light};`}
        }
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 6px;
        height: 100%;
        background-color: ${categoryColor};
      }
    `
  }}
`

const StActivityCardContent = styled(FlexColumn)`
  gap: 4px;
`

const StDescription = styled(Span)<{ color?: string }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ color }) => color};
`
